import {
  AccessTimeOutlined,
  ApartmentOutlined,
  BusinessOutlined,
  Home,
  Info,
  LocationOnOutlined,
  PersonPin,
  PublicOutlined,
  Refresh
} from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";

import { FormRowSwitch, FormSection, NumberStepper, SelectInput, TabPanel, TextInput } from "components";
import { COLORS } from "assets";
import {
  adminModelCatalog,
  COUNTRY_CCA3,
  ENDPOINTS,
  filterModelsForUI,
  MODEL_LABELS,
  MODELS,
  sensitivityFieldsForModels,
  TIMEZONES,
  VIRTUOSE_RTSP_METHOD_LABELS,
  VIRTUOSE_RTSP_METHODS
} from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import { useSnackbar } from "notistack";
import { FormEventHandler, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ISites, ModelTypes } from "types";
import { IncidentJurisdictionPanel } from "./incidentJurisdiction";
import SiteMembers from "./members";

const defaultState = {
  name: "",
  address: "",
  city: "",
  startHour: "",
  virtuoseRTSPMethod: "",
  endHour: "",
  country: "",
  timezone: "",
  overallST: "",
  zoneST: "",
  gestureST: "",
  weaponST: "",
  fightST: "",
  passedOutST: "",
  abusiveST: "",
  isVirtuoseClient: "",
  virtuoseTenantID: "",
  messageGroupId: "",
  camCount: "",
  serverId: "",
  cameraUsername: "",
  cameraPassword: ""
};

interface Data {
  site: ISites;
}

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

const overallSensitivity = { key: "overallST" as const, labelKey: "Overall Sensitivity" };

export default function Site() {
  const navigate = useNavigate();
  const members = useMemo(() => Boolean(window?.location?.search?.includes("members")), []);
  const [tab, setTab] = useState(0);
  const [reload, setReload] = useState(false);
  const [state, setState] = useState({
    ...defaultState,
    country: "CAN",
    virtuoseRTSPMethod: "cameraIpAndProfile",
    startHour: "09:00",
    endHour: "18:00",
    timezone: "(UTC-05:00) Canada",
    overallST: "5",
    zoneST: "5",
    gestureST: "5",
    weaponST: "5",
    fightST: "5",
    passedOutST: "5",
    abusiveST: "5",
    camCount: "28",
    serverId: "1",
    isVirtuoseClient: "false"
  });
  const [enabledModels, setEnabledModels] = useState<ModelTypes[]>(() => adminModelCatalog());
  const sensitivityFields = useMemo(
    () => [overallSensitivity, ...sensitivityFieldsForModels(enabledModels)],
    [enabledModels]
  );
  const [errors, setErrors] = useState(defaultState);

  const t = useTranslation();

  const disabledSubmit = useMemo(
    () => !!(errors.name || !state.name || errors.address || !state.address || errors.city || !state.city),
    [state, errors]
  );

  const { isLoading, response, error, callFetch: callFetchPost } = useFetch("PUT");

  const {
    isLoading: isLoadingGet,
    response: responseGet,
    error: errorGet,
    callFetch: callFetchGet
  } = useFetch<Data>("GET");

  const [site] = useMemo(() => {
    const siteDetails = responseGet?.data?.site;
    if (!siteDetails) return [null];
    setState(
      (prevState) =>
        ({
          ...prevState,
          ...siteDetails,
          isVirtuoseClient: siteDetails.isVirtuoseClient + "",
          timezone: TIMEZONES.find((item) => item.timezone === siteDetails.timezone)?.label || defaultState.timezone
        } as any)
    );
    setEnabledModels(filterModelsForUI(siteDetails.enabledModels as ModelTypes[]));
    return [siteDetails];
  }, [responseGet]);

  const { enqueueSnackbar } = useSnackbar();

  const onReload = useCallback(() => setReload((prevState) => !prevState), []);

  const setSensitivity = useCallback((key: keyof typeof defaultState, value: number) => {
    setState((prev) => ({ ...prev, [key]: String(value) }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  }, []);

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      console.log("state", state);
      if (!site) return;
      let body: any = {};
      if (site.country !== state.country) {
        body.country = state.country;
      }
      const timezone = TIMEZONES.find((item) => item.value === state.timezone)?.timezone;
      if (site.timezone !== timezone) {
        body.timezone = timezone;
      }
      if (site.name !== state.name) {
        body.name = state.name;
      }
      if (site.address !== state.address) {
        body.address = state.address;
      }
      if (site.city !== state.city) {
        body.city = state.city;
      }
      if (site.startHour !== state.startHour) {
        body.startHour = state.startHour;
      }
      if (site.endHour !== state.endHour) {
        body.endHour = state.endHour;
      }
      if (site.overallST + "" !== state.overallST) {
        body.overallST = state.overallST;
      }
      if (site.zoneST + "" !== state.zoneST) {
        body.zoneST = state.zoneST;
      }
      if (site.gestureST + "" !== state.gestureST) {
        body.gestureST = state.gestureST;
      }
      if (site.weaponST + "" !== state.weaponST) {
        body.weaponST = state.weaponST;
      }
      if (site.fightST + "" !== state.fightST) {
        body.fightST = state.fightST;
      }
      if (site.passedOutST + "" !== state.passedOutST) {
        body.passedOutST = state.passedOutST;
      }
      if ((site.abusiveST ?? 5) + "" !== state.abusiveST) {
        body.abusiveST = state.abusiveST;
      }
      if (site.camCount + "" !== state.camCount) {
        body.camCount = state.camCount;
      }
      if (site.serverId + "" !== state.serverId) {
        body.serverId = state.serverId;
      }
      if (site.cameraUsername + "" !== state.cameraUsername) {
        body.cameraUsername = state.cameraUsername;
      }
      if (site.cameraPassword + "" !== state.cameraPassword) {
        body.cameraPassword = state.cameraPassword;
      }
      if (site.virtuoseTenantID !== state.virtuoseTenantID) {
        body.isVirtuoseClient = state.isVirtuoseClient;
        body.virtuoseTenantID = state.virtuoseTenantID;
      }
      if (site.messageGroupId !== state.messageGroupId) {
        body.isVirtuoseClient = state.isVirtuoseClient;
        body.messageGroupId = state.messageGroupId;
      }
      if (site.virtuoseRTSPMethod !== state.virtuoseRTSPMethod) {
        body.virtuoseRTSPMethod = state.virtuoseRTSPMethod;
      }
      const prevModels = filterModelsForUI(site.enabledModels as ModelTypes[]).sort().join();
      const nextModels = [...enabledModels].sort().join();
      if (prevModels !== nextModels) {
        body.enabledModels = enabledModels;
      }
      if (Object.keys(body).length < 0) {
        enqueueSnackbar("Nothing to update", { variant: "error" });
      }
      callFetchPost(ENDPOINTS.sites + `/${site._id}`, {
        body: JSON.stringify(body)
      }).catch(console.error);
    },
    [state, site, enabledModels, callFetchPost, enqueueSnackbar]
  );

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  }, []);

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      // navigate("/sites");
    }
  }, [response, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  useEffect(() => {
    const split = window?.location?.pathname?.split("/");
    if (split[2]) {
      callFetchGet(ENDPOINTS.sites + `/${split[2]}`).catch(console.error);
    }
  }, [callFetchGet, reload]);

  useEffect(() => {
    if (members) {
      setTab(1);
    }
  }, [members]);

  // useEffect(() => {
  //   console.log("site", site);
  // }, [site]);

  // useEffect(() => {
  //   console.log("state", state);
  // }, [state]);

  return (
    <PrivateLayout canAccess={(type, role) => !(type === "user" && role === "user")}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Breadcrumbs aria-label="breadcrumb">
          <Tooltip title={t["Home"]}>
            <IconButton LinkComponent={Link} {...{ to: "/" }} size="small" sx={{ color: COLORS.surface.secondaryText }}>
              <Home fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            LinkComponent={Link}
            {...{ to: "/sites" }}
            sx={{ color: COLORS.surface.secondaryText, textTransform: "none", fontWeight: 500, minWidth: 0, p: 0 }}
          >
            {t["Sites"]}
          </Button>
          {errorGet ? (
            <Typography color="error.main">{t["Invalid site id"]}</Typography>
          ) : (
            <Typography color="text.primary" sx={{ fontWeight: 500 }}>
              {site?.name}
            </Typography>
          )}
        </Breadcrumbs>
        {isLoadingGet ? (
          <IconButton
            disabled
            sx={{
              width: 40,
              height: 40,
              color: COLORS.surface.primaryText,
              border: `1px solid ${COLORS.surface.border}`,
              bgcolor: COLORS.surface.card,
              borderRadius: `${COLORS.radius.sm}px`
            }}
          >
            <CircularProgress size={20} sx={{ color: COLORS.primary }} />
          </IconButton>
        ) : (
          <Tooltip title={t["Refresh"]}>
            <IconButton
              onClick={onReload}
              sx={{
                bgcolor: COLORS.surface.card,
                color: COLORS.surface.primaryText,
                border: `1px solid ${COLORS.surface.border}`,
                borderRadius: `${COLORS.radius.sm}px`,
                "&:hover": { bgcolor: COLORS.surface.tableHeader }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {site && (
        <Fragment>
          <Box display="flex" justifyContent="center" alignItems="center" width="100%" mb={3}>
            <Tabs value={tab} onChange={handleTabChange} aria-label="site tabs">
              <Tab icon={<Info />} label={t["Info"]} value={0} />
              <Tab icon={<PersonPin />} label={t["Members"]} value={1} />
            </Tabs>
          </Box>
          <TabPanel value={tab} index={0}>
            <Box component="form" onSubmit={onSubmit} width="100%">
              <FormSection title={t["Site Information"]} description={t["Site Information description"]}>
                <Stack spacing={2.5}>
                  <TextInput
                    value={state.name}
                    label={t["Name"]}
                    type="text"
                    placeholder={t["e.g NeuraVue Dubai Office"]}
                    error={!!errors.name}
                    helperText={errors.name}
                    startAdornment={<BusinessOutlined sx={iconSx} />}
                    sx={fieldSx}
                    onChange={(e) => {
                      setState((prevState) => ({ ...prevState, name: e.target.value }));
                      setErrors((prevState) => ({ ...prevState, name: "" }));
                    }}
                    fullWidth
                    required
                    autoFocus
                  />
                  <TextInput
                    value={state.address}
                    label={t["Address"]}
                    type="text"
                    placeholder={t["e.g Apartment/Building A12X, Street 123"]}
                    error={!!errors.address}
                    helperText={errors.address}
                    startAdornment={<LocationOnOutlined sx={iconSx} />}
                    sx={fieldSx}
                    onChange={(e) => {
                      setState((prevState) => ({ ...prevState, address: e.target.value }));
                      setErrors((prevState) => ({ ...prevState, address: "" }));
                    }}
                    fullWidth
                    required
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextInput
                        value={state.city}
                        label={t["City"]}
                        type="text"
                        placeholder={t["e.g Dubai"]}
                        error={!!errors.city}
                        helperText={errors.city}
                        startAdornment={<ApartmentOutlined sx={iconSx} />}
                        sx={fieldSx}
                        onChange={(e) => {
                          setState((prevState) => ({ ...prevState, city: e.target.value }));
                          setErrors((prevState) => ({ ...prevState, city: "" }));
                        }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SelectInput
                        id="country-select"
                        data={COUNTRY_CCA3}
                        value={state.country}
                        label={t["Country"]}
                        helperText=""
                        startAdornment={<PublicOutlined sx={iconSx} />}
                        onChange={({ target: { value } }) => {
                          if (!value) return;
                          setState((prevState) => ({ ...prevState, country: value as string }));
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SelectInput
                        id="timezone-select"
                        data={TIMEZONES}
                        value={state.timezone}
                        label={t["Time Zone"]}
                        helperText=""
                        startAdornment={<AccessTimeOutlined sx={iconSx} />}
                        onChange={({ target: { value } }) => {
                          if (!value) return;
                          setState((prevState) => ({ ...prevState, timezone: value as string }));
                        }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </FormSection>
              <FormSection title={t["Operating Hours"]} description={t["Operating Hours description"]}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextInput
                      value={state.startHour}
                      label={t["Start Time"]}
                      type="time"
                      error={!!errors.startHour}
                      helperText={errors.startHour}
                      startAdornment={<AccessTimeOutlined sx={iconSx} />}
                      sx={fieldSx}
                      onChange={({ target: { value } }) => {
                        setState((prevState) => ({ ...prevState, startHour: value }));
                        setErrors((prevState) => ({ ...prevState, startHour: "" }));
                      }}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextInput
                      value={state.endHour}
                      label={t["End Time"]}
                      type="time"
                      error={!!errors.endHour}
                      helperText={errors.endHour}
                      startAdornment={<AccessTimeOutlined sx={iconSx} />}
                      sx={fieldSx}
                      onChange={({ target: { value } }) => {
                        setState((prevState) => ({ ...prevState, endHour: value }));
                        setErrors((prevState) => ({ ...prevState, endHour: "" }));
                      }}
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <FormSection title={t["Detection Sensitivity"]} description={t["Detection Sensitivity description"]}>
                <Grid container spacing={2}>
                  {sensitivityFields.map(({ key, labelKey }) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <NumberStepper
                        label={t[labelKey]}
                        value={Number.parseInt(state[key] || "5", 10)}
                        min={1}
                        max={10}
                        error={errors[key]}
                        onChange={(value) => setSensitivity(key, value)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormSection>

              <FormSection title={t["Detection Features"]} description={t["Detection Features description"]}>
                <Stack divider={<Divider sx={{ borderColor: COLORS.surface.border }} />}>
                  {adminModelCatalog().map((model) => {
                    const labelIndex = MODELS.indexOf(model);
                    return (
                    <FormRowSwitch
                      key={model}
                      label={t[MODEL_LABELS[labelIndex]]}
                      checked={enabledModels.includes(model)}
                      onChange={(checked) => {
                        setEnabledModels((prev) => (checked ? [...prev, model] : prev.filter((item) => item !== model)));
                      }}
                    />
                    );
                  })}
                </Stack>
              </FormSection>
              <FormSection title={t["Client Configuration"]} description={t["Client Configuration description"]}>
                <FormRowSwitch
                  label={t["Virtuose Client"]}
                  checked={state.isVirtuoseClient === "true"}
                  onChange={(checked) => setState((prevState) => ({ ...prevState, isVirtuoseClient: String(checked) }))}
                />
                {state.isVirtuoseClient === "true" && (
                  <Fragment>
                    <Divider sx={{ borderColor: COLORS.surface.border, my: 2 }} />
                    <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextInput
                        value={state.virtuoseTenantID}
                        label={t["Virtuose Tenant ID"]}
                        type="text"
                        placeholder="e.g Groupvite"
                        error={!!errors.virtuoseTenantID}
                        helperText={errors.virtuoseTenantID}
                        onChange={({ target }) => {
                          setState((prevState) => ({
                            ...prevState,
                            virtuoseTenantID: target.value
                          }));
                          setErrors((prevState) => ({
                            ...prevState,
                            virtuoseTenantID: ""
                          }));
                        }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextInput
                        value={state.messageGroupId}
                        label={t["Virtuose Message Group Id"]}
                        type="text"
                        placeholder="e.g Groupvite"
                        error={!!errors.messageGroupId}
                        helperText={errors.messageGroupId}
                        onChange={({ target }) => {
                          setState((prevState) => ({
                            ...prevState,
                            messageGroupId: target.value
                          }));
                          setErrors((prevState) => ({
                            ...prevState,
                            messageGroupId: ""
                          }));
                        }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextInput
                        value={state.camCount}
                        label={t["Camera Count"]}
                        type="number"
                        error={!!errors.camCount}
                        helperText={errors.camCount}
                        onChange={({ target }) => {
                          setState((prevState) => ({
                            ...prevState,
                            camCount: target.value
                          }));
                          setErrors((prevState) => ({
                            ...prevState,
                            camCount: ""
                          }));
                        }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextInput
                        value={state.serverId}
                        label={t["Server ID"]}
                        type="number"
                        placeholder="e.g 1"
                        error={!!errors.serverId}
                        helperText={errors.serverId}
                        onChange={({ target }) => {
                          setState((prevState) => ({
                            ...prevState,
                            serverId: target.value
                          }));
                          setErrors((prevState) => ({
                            ...prevState,
                            serverId: ""
                          }));
                        }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextInput
                        value={state.cameraUsername || ""}
                        label={t["Camera Username"]}
                        type="text"
                        placeholder="e.g groupvite"
                        error={!!errors.cameraUsername}
                        helperText={errors.cameraUsername}
                        onChange={({ target }) => {
                          setState((prevState) => ({
                            ...prevState,
                            cameraUsername: target.value
                          }));
                          setErrors((prevState) => ({
                            ...prevState,
                            cameraUsername: ""
                          }));
                        }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextInput
                        required
                        fullWidth
                        id="camera-password"
                        name="camera-password"
                        type="password"
                        autoComplete="new-password"
                        label={t["Camera Password"]}
                        aria-describedby="Enter new password"
                        value={state.cameraPassword || ""}
                        helperText={errors?.cameraPassword}
                        error={!!errors?.cameraPassword}
                        sx={{ mb: 2 }}
                        onChange={(e) => {
                          e?.preventDefault();
                          setState((prevState) => ({
                            ...prevState,
                            cameraPassword: e.target.value
                          }));
                          setErrors((prevState) => ({
                            ...prevState,
                            cameraPassword: ""
                          }));
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SelectInput
                        id="virtuoseRTSPMethod-select"
                        data={VIRTUOSE_RTSP_METHODS.map((elm) => ({
                          label: t[VIRTUOSE_RTSP_METHOD_LABELS[elm]],
                          value: elm
                        }))}
                        value={state.virtuoseRTSPMethod}
                        label={t["RTSP Method"]}
                        onChange={({ target: { value } }) => {
                          setState((prevState) => ({
                            ...prevState,
                            virtuoseRTSPMethod: value as string
                          }));
                        }}
                        fullWidth
                      />
                    </Grid>
                    </Grid>
                  </Fragment>
                )}
              </FormSection>

              <IncidentJurisdictionPanel />
              <Box display="flex" justifyContent="flex-end" gap={1.5} mt={1} mb={2}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate("/sites")}
                  sx={{
                    minWidth: 120,
                    height: 44,
                    borderRadius: `${COLORS.radius.md}px`,
                    borderColor: COLORS.surface.border,
                    color: COLORS.surface.primaryText,
                    fontWeight: 500,
                    textTransform: "none"
                  }}
                >
                  {t["Cancel"]}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={disabledSubmit}
                  endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
                  sx={{
                    minWidth: 140,
                    height: 44,
                    borderRadius: `${COLORS.radius.md}px`,
                    fontWeight: 500,
                    textTransform: "none",
                    color: COLORS.white,
                    backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
                      filter: "brightness(1.05)",
                      boxShadow: "none"
                    }
                  }}
                >
                  {t["Update"]}
                </Button>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <SiteMembers site={site} onSuccess={onReload} />
          </TabPanel>
        </Fragment>
      )}
    </PrivateLayout>
  );
}
